import { syncLinkedItemZonesFromTextures } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import { LinkedItem } from '@types'
import { getZonesFromGLB } from '@utils/sceneObjects/getZonesFromGLB'
import { mergeGlbZoneTextureWithUserApplied } from '@utils/sceneObjects/linked/reconcileTexturesAfterUnitChange'
import {
  resolveUserTextureByDisplayName,
  resolveUserTextureForGlbZone,
  textureZoneAlreadyProcessed,
} from '@utils/sceneObjects/linked/linkedZoneTextureMapping'
import { StoredTextureLike } from '@utils/sceneObjects/linked/textureCatalogApi'

export function collectValidZonesFromUnitAcabados(acabados: unknown): Set<string> {
  const valid = new Set<string>()
  if (!Array.isArray(acabados)) return valid
  acabados.forEach((acabado: { zona?: string; descripcion?: string }) => {
    if (acabado.zona) valid.add(acabado.zona)
    if (acabado.descripcion) valid.add(acabado.descripcion)
  })
  return valid
}

export function isTextureValidForUnitAcabados(
  texture: Pick<StoredTextureLike, 'zoneDisplayName' | 'zone' | 'zoneName'>,
  validZones: Set<string>,
): boolean {
  if (texture.zoneDisplayName && validZones.has(texture.zoneDisplayName)) return true
  if (texture.zone && validZones.has(texture.zone)) return true
  if (texture.zoneName && validZones.has(texture.zoneName)) return true
  if (texture.zoneDisplayName === 'Armazon') return true
  return false
}

export type RebuildLinkedZonesParams = {
  item: LinkedItem
  newUnidadData: {
    acabados?: unknown
    codigo?: string
  }
  glbSceneChildren: unknown
  acabadosZonasExtra?: { zonas: { idAcabadoUnidad: number; zona: string }[] }
  userAppliedTexturesByZoneName?: Map<string, StoredTextureLike>
  isVitrina?: boolean
  itemId: string
  source: string
}

/**
 * Reconstruye zones + textures[] desde el GLB y acabados del backend.
 * Elimina zonas huérfanas (p. ej. Gola al pasar a Lineal) y preserva texturas de usuario.
 */
export function rebuildLinkedItemZonesFromUnit(params: RebuildLinkedZonesParams): void {
  const {
    item,
    newUnidadData,
    glbSceneChildren,
    acabadosZonasExtra,
    userAppliedTexturesByZoneName = new Map(),
    isVitrina = false,
    itemId,
    source,
  } = params

  const { zones, textures: glbTextures } = getZonesFromGLB(
    glbSceneChildren as Parameters<typeof getZonesFromGLB>[0],
    newUnidadData.acabados as Parameters<typeof getZonesFromGLB>[1],
    acabadosZonasExtra,
  )

  const validZonesFromAPI = collectValidZonesFromUnitAcabados(newUnidadData.acabados)
  const existingZones = { ...item.zones }
  const zonesCopy: Record<string, unknown> = JSON.parse(JSON.stringify(zones))

  Object.keys(zonesCopy).forEach((zoneKey) => {
    const newZone = zonesCopy[zoneKey] as {
      material?: { zoneDisplayName?: string; zone?: string }
      centerInfo?: unknown
      contextInfo?: unknown
      [key: string]: unknown
    }
    const displayName = newZone.material?.zoneDisplayName || newZone.material?.zone || ''

    let existingZone: (typeof existingZones)[string] | null = existingZones[zoneKey] ?? null

    if (!existingZone && displayName) {
      const match = Object.entries(existingZones).find(
        ([, zone]) =>
          zone?.material?.zoneDisplayName === displayName || zone?.material?.zone === displayName,
      )
      existingZone = match?.[1] ?? null
    }

    if (existingZone) {
      zonesCopy[zoneKey] = {
        ...newZone,
        ...(existingZone.centerInfo && { centerInfo: existingZone.centerInfo }),
        ...(existingZone.contextInfo && { contextInfo: existingZone.contextInfo }),
        ...Object.keys(existingZone).reduce(
          (acc: Record<string, unknown>, key) => {
            if (key.includes('Info') || key.includes('Context') || key.includes('Center')) {
              acc[key] = existingZone![key as keyof typeof existingZone]
            }
            return acc
          },
          {} as Record<string, unknown>,
        ),
      }
    }
  })

  item.zones = zonesCopy as LinkedItem['zones']

  const oldTextures = [...item.textures]
  const newTextures: StoredTextureLike[] = []
  const processedZoneNames = new Set<string>()
  const processedDisplayNames = new Set<string>()
  const updatedUserTexturesByZoneName = new Map<string, StoredTextureLike>()

  oldTextures.forEach((texture) => {
    if (texture.zoneName) {
      updatedUserTexturesByZoneName.set(texture.zoneName, texture)
    }
  })

  const markProcessed = (zoneName: string, displayName: string, zone: string) => {
    if (zoneName) processedZoneNames.add(zoneName)
    if (displayName) processedDisplayNames.add(displayName)
    if (zone) processedDisplayNames.add(zone)
  }

  glbTextures.forEach((glbTexture) => {
    const zoneName = glbTexture.zoneName ?? ''
    const zoneDisplayName = glbTexture.zoneDisplayName ?? ''
    const zone = glbTexture.zone ?? ''

    const isValidZone =
      validZonesFromAPI.has(zoneDisplayName) ||
      validZonesFromAPI.has(zone) ||
      zoneDisplayName === 'Armazon'

    if (!isValidZone || !zoneName) return

    // Emparejar por zona LÓGICA (displayName) primero: las claves de mesh se
    // desplazan al cambiar de unidad (Gola↔Lineal) y romperían la asignación.
    let userTexture = resolveUserTextureByDisplayName(zoneDisplayName, zone, oldTextures)

    if (!userTexture) {
      userTexture = resolveUserTextureForGlbZone(
        zoneName,
        zoneDisplayName,
        oldTextures,
        updatedUserTexturesByZoneName,
        userAppliedTexturesByZoneName,
      )
    }

    if (isVitrina && !userTexture) {
      for (const texture of oldTextures) {
        if (
          texture.zoneDisplayName?.toLowerCase() === zoneDisplayName?.toLowerCase() ||
          (zoneName && texture.zoneName && texture.zoneName === zoneName) ||
          (zoneName &&
            texture.zoneName &&
            texture.zoneName.includes(zoneName.split('_')[0]))
        ) {
          markProcessed(zoneName, zoneDisplayName, zone)
          newTextures.push(mergeGlbZoneTextureWithUserApplied(glbTexture, texture))
          return
        }
      }
    }

    if (userTexture) {
      markProcessed(zoneName, zoneDisplayName, zone)
      newTextures.push(mergeGlbZoneTextureWithUserApplied(glbTexture, userTexture))
    } else {
      markProcessed(zoneName, zoneDisplayName, zone)
      newTextures.push({
        ...glbTexture,
        zoneName,
        zoneDisplayName,
        zone: zoneDisplayName,
      })
    }
  })

  oldTextures.forEach((existingTexture) => {
    const alreadyProcessedByZone = textureZoneAlreadyProcessed(
      processedZoneNames,
      existingTexture,
      newTextures,
    )

    // Evitar duplicar zonas ya reconstruidas (la misma zona lógica puede tener una
    // clave de mesh distinta entre la unidad anterior y la nueva).
    const alreadyProcessedByDisplay =
      (existingTexture.zoneDisplayName != null &&
        processedDisplayNames.has(existingTexture.zoneDisplayName)) ||
      (existingTexture.zone != null && processedDisplayNames.has(existingTexture.zone))

    if (
      !alreadyProcessedByZone &&
      !alreadyProcessedByDisplay &&
      isTextureValidForUnitAcabados(existingTexture, validZonesFromAPI)
    ) {
      newTextures.push(existingTexture)
    }
  })

  item.textures = newTextures as LinkedItem['textures']

  const validZoneNames = new Set<string>()
  newTextures.forEach((texture) => {
    if (texture.zoneName) validZoneNames.add(texture.zoneName)
  })

  const finalFilteredZones: LinkedItem['zones'] = {}
  Object.keys(item.zones).forEach((zoneKey) => {
    if (validZoneNames.has(zoneKey)) {
      finalFilteredZones[zoneKey] = item.zones[zoneKey]
    }
  })
  item.zones = finalFilteredZones

  syncLinkedItemZonesFromTextures(item, {
    itemId,
    catalogID: String(item.catalogID ?? newUnidadData.codigo ?? ''),
    source,
  })
}
